import { type ButtonHTMLAttributes, type Ref } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium text-sm transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00ab81] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-[#00ab81] text-white hover:bg-[#00926f] active:bg-[#00765a] shadow-sm',
        secondary:
          'bg-surface-100 text-surface-700 hover:bg-surface-200 active:bg-surface-300 border border-[#919eab33] dark:bg-[#252f3a] dark:text-white dark:hover:bg-[#333d47] dark:border-[#919eab29]',
        ghost:
          'text-surface-500 hover:bg-surface-100 hover:text-surface-900 active:bg-surface-200 dark:text-[#919eab] dark:hover:bg-[#252f3a] dark:hover:text-white',
        danger:
          'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
      },
      size: {
        sm: 'h-8 px-3 text-xs gap-1.5',
        md: 'h-10 px-4 text-sm gap-2',
        lg: 'h-12 px-6 text-base gap-2.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  ref?: Ref<HTMLButtonElement>
}

export function Button({ className, variant, size, loading, icon, iconPosition = 'left', children, disabled, ref, ...props }: ButtonProps) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      {!loading && icon && iconPosition === 'left' && (
        <span className="shrink-0" aria-hidden="true">{icon}</span>
      )}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span className="shrink-0" aria-hidden="true">{icon}</span>
      )}
    </button>
  )
}
